// React component

import { motion } from "framer-motion";
import { Badge } from "@/components/ui/badge";
import { useCallback, useRef, useState, useEffect } from "react";

interface DataPoint {
  views: number;
  viewsGrowth: number;
  clicks: number;
  clicksGrowth: number;
  viewsY: number;
  clicksY: number;
}

// Parse the SVG path coordinates
const viewsPath =
  "M0 312H42.6087L85.2174 280.966H127.065L168.913 264.511L210.761 280.966L252.609 250.469L294.457 197.476L336.304 168.979H378.152L420 198.976H461.848L503.696 168.979H545.543L587.391 113.486H629.239L671.087 57.493L712.935 30.4964H754.783L797.391 1H840";
const clicksPath =
  "M0 147H42.6087L85.2174 135.965H127.065L168.913 127.467L210.761 117.469L252.609 126.967L294.457 117.469L336.304 88.9769L378.152 108.972L420 88.9769L461.848 108.972L503.696 68.9822H545.543L587.391 91.4757H629.239L671.087 47.4878L712.935 30.4923H754.783L797.391 1H840";

const generateData = (): DataPoint[] => {
  const data: DataPoint[] = [];

  // Extract points from the SVG paths
  const viewsCoords =
    viewsPath.match(/[HLM]\s*\d+(?:\.\d+)?\s*(?:\d+(?:\.\d+)?)?/g) || [];
  const clicksCoords =
    clicksPath.match(/[HLM]\s*\d+(?:\.\d+)?\s*(?:\d+(?:\.\d+)?)?/g) || [];

  let lastViewsX = 0;
  let lastViewsY = 312;
  let lastClicksX = 0;
  let lastClicksY = 147;

  // Create a mapping of x positions to data points
  const points = viewsCoords.map((viewsCmd, index) => {
    const clicksCmd = clicksCoords[index];

    // Parse views coordinates
    const viewsNums = viewsCmd.match(/\d+(?:\.\d+)?/g)?.map(Number) || [];
    const viewsX =
      viewsCmd[0] === "H" ? viewsNums[0] : viewsNums[0] || lastViewsX;
    const viewsY =
      viewsCmd[0] === "H" ? lastViewsY : viewsNums[1] || lastViewsY;

    // Parse clicks coordinates
    const clicksNums =
      (clicksCmd || "").match(/\d+(?:\.\d+)?/g)?.map(Number) || [];
    const clicksX =
      clicksCmd?.[0] === "H" ? clicksNums[0] : clicksNums[0] || lastClicksX;
    const clicksY =
      clicksCmd?.[0] === "H" ? lastClicksY : clicksNums[1] || lastClicksY;

    lastViewsX = viewsX;
    lastViewsY = viewsY;
    lastClicksX = clicksX;
    lastClicksY = clicksY;

    return { viewsX, viewsY, clicksX, clicksY };
  });

  // Interpolate 30 points from the path points
  for (let i = 0; i < 30; i++) {
    const progress = i / 29; // 0 to 1
    const index = Math.min(
      Math.floor(progress * (points.length - 1)),
      points.length - 2,
    );
    const fraction = (progress * (points.length - 1)) % 1;

    const current = points[index];
    const next = points[index + 1];

    // Interpolate between points
    const viewsY = current.viewsY + (next.viewsY - current.viewsY) * fraction;
    const clicksY =
      current.clicksY + (next.clicksY - current.clicksY) * fraction;

    // Convert Y coordinates to actual values
    const views = Math.round(7000 - (viewsY / 312) * 6000);
    const clicks = Math.round(3500 - (clicksY / 147) * 2500);

    const prevViews = i > 0 ? data[i - 1].views : views;
    const prevClicks = i > 0 ? data[i - 1].clicks : clicks;

    const viewsGrowth =
      i > 0 ? Math.round(((views - prevViews) / prevViews) * 100) : 0;
    const clicksGrowth =
      i > 0 ? Math.round(((clicks - prevClicks) / prevClicks) * 100) : 0;

    data.push({
      views,
      viewsGrowth,
      clicks,
      clicksGrowth,
      viewsY,
      clicksY,
    });
  }

  return data;
};

const AnalyticsDashboard = () => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [data] = useState<DataPoint[]>(generateData());
  const [hoveredIndex, setHoveredIndex] = useState<number>(15);
  const [mouseX, setMouseX] = useState<number>(0);
  const [isMobile, setIsMobile] = useState<boolean>(false);

  // Detect mobile device on component mount
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };

    // Initial check
    checkMobile();

    // Check on resize
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  const handleMouseMove = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      if (!containerRef.current) return;

      const rect = containerRef.current.getBoundingClientRect();
      const graphWidth = rect.width - 84; // Width of the actual graph area
      const x = Math.max(
        0,
        Math.min(graphWidth, event.clientX - rect.left - 84),
      );
      const index = Math.min(
        Math.floor((x / graphWidth) * data.length),
        data.length - 1,
      );

      setHoveredIndex(Math.max(0, index));
      setMouseX(x + 84);
    },
    [data.length],
  );

  // Handle touch events for mobile
  const handleTouchMove = useCallback(
    (event: React.TouchEvent<HTMLDivElement>) => {
      if (!containerRef.current || event.touches.length === 0) return;

      const touch = event.touches[0];
      const rect = containerRef.current.getBoundingClientRect();
      const graphWidth = rect.width - 84; // Width of the actual graph area
      const x = Math.max(
        0,
        Math.min(graphWidth, touch.clientX - rect.left - 84),
      );
      const index = Math.min(
        Math.floor((x / graphWidth) * data.length),
        data.length - 1,
      );

      setHoveredIndex(Math.max(0, index));
      setMouseX(x + 84);

      // Prevent page scrolling when interacting with the chart
      event.preventDefault();
    },
    [data.length],
  );

  const handleMouseLeave = () => {
    setHoveredIndex(15);
    setMouseX(0);
  };

  const handleTouchEnd = () => {
    if (isMobile) {
      // On mobile, don't reset on touch end to keep selection visible
      return;
    }
    handleMouseLeave();
  };

  const getXPosition = (index: number): number => {
    return (index / (data.length - 1)) * 840;
  };

  const getScaledXPosition = (index: number): number => {
    if (!containerRef.current) return 0;
    const graphWidth = containerRef.current.getBoundingClientRect().width - 84;
    return (index / (data.length - 1)) * graphWidth;
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="w-full rounded-xl bg-card p-6 shadow-lg"
      data-oid="ejlw0na"
    >
      <div className="analytics-graph_visual" data-oid="e8vg78b">
        <div
          className="analytics-graph_container analytics-graph_desktop"
          data-oid="34vp6oz"
        >
          <div
            ref={containerRef}
            aria-label="A chart showing two lines that represent views and clicks of a website."
            className="relative h-[320px] w-full cursor-crosshair touch-manipulation"
            role="img"
            onMouseMove={handleMouseMove}
            onMouseLeave={handleMouseLeave}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
            data-oid="4qjd2:b"
          >
            {/* Grid Lines */}
            <div
              className="absolute inset-0 flex flex-col justify-between"
              data-oid="87oj28a"
            >
              {[7000, 6000, 5000, 4000, 3000, 2000, 1000].map((value) => (
                <div
                  key={value}
                  className="flex items-center gap-2 text-sm text-muted-foreground"
                  data-oid="awkld2k"
                >
                  <span className="w-16 text-right" data-oid="trid27:">
                    {value.toLocaleString()}
                  </span>
                  <div
                    className="h-[1px] flex-1 bg-border/50"
                    data-oid="uog_:h6"
                  />
                </div>
              ))}
            </div>

            {/* Graph Lines */}
            <div className="absolute inset-0 left-[84px]" data-oid="qdc:9h-">
              <svg
                className="absolute inset-0"
                viewBox="0 0 840 320"
                fill="none"
                data-oid="mwl165g"
              >
                {/* Views Line */}
                <path
                  d={viewsPath}
                  stroke="url(#large-blue)"
                  strokeWidth="2"
                  className="text-blue-500"
                  vectorEffect="non-scaling-stroke"
                  data-oid="w777ikv"
                />

                {mouseX > 0 && (
                  <g data-oid="l54qh45">
                    <circle
                      cx={getXPosition(hoveredIndex)}
                      cy={data[hoveredIndex]?.viewsY}
                      r="7"
                      fill="var(--background)"
                      className="stroke-blue-500 transition-all duration-300 ease-out"
                      strokeWidth="1.5"
                      vectorEffect="non-scaling-size"
                      data-oid="41jtap4"
                    />

                    <circle
                      cx={getXPosition(hoveredIndex)}
                      cy={data[hoveredIndex]?.viewsY}
                      r="4"
                      className="fill-blue-500 transition-all duration-300 ease-out"
                      vectorEffect="non-scaling-size"
                      data-oid="y64j_y4"
                    />
                  </g>
                )}
                <defs data-oid="rlu-dtj">
                  <linearGradient id="large-blue" data-oid="8srthba">
                    <stop
                      offset="5%"
                      stopColor="currentColor"
                      stopOpacity="0"
                      data-oid="7_:1hjl"
                    />
                    <stop
                      offset="40%"
                      stopColor="currentColor"
                      data-oid="7.i-a.j"
                    />
                  </linearGradient>
                </defs>
              </svg>

              <svg
                className="absolute inset-0"
                viewBox="0 0 840 148"
                data-oid="ntxumqr"
              >
                {/* Clicks Line */}
                <path
                  d={clicksPath}
                  stroke="url(#large-teal)"
                  strokeWidth="2"
                  className="text-teal-500"
                  vectorEffect="non-scaling-stroke"
                  data-oid="1:c_43r"
                />

                {mouseX > 0 && (
                  <g data-oid="duo2.3k">
                    <circle
                      cx={getXPosition(hoveredIndex)}
                      cy={data[hoveredIndex]?.clicksY}
                      r="7"
                      fill="var(--background)"
                      className="stroke-teal-500 transition-all duration-300 ease-out"
                      strokeWidth="1.5"
                      vectorEffect="non-scaling-size"
                      data-oid="pukb9g."
                    />

                    <circle
                      cx={getXPosition(hoveredIndex)}
                      cy={data[hoveredIndex]?.clicksY}
                      r="4"
                      className="fill-teal-500 transition-all duration-300 ease-out"
                      vectorEffect="non-scaling-size"
                      data-oid=".gkut7k"
                    />
                  </g>
                )}
                <defs data-oid="bhfsfie">
                  <linearGradient id="large-teal" data-oid="ipr4jaz">
                    <stop
                      offset="5%"
                      stopColor="currentColor"
                      stopOpacity="0"
                      data-oid="w0hhxlu"
                    />
                    <stop
                      offset="40%"
                      stopColor="currentColor"
                      data-oid="ahd1dnn"
                    />
                  </linearGradient>
                </defs>
              </svg>

              {/* Hover Line */}
              <div
                className="absolute top-0 h-full w-[6px] bg-primary/5 transition-all duration-300 ease-out"
                style={{
                  transform: `translateX(${getScaledXPosition(hoveredIndex) - 3}px)`,
                  opacity: mouseX > 0 ? 1 : 0,
                }}
                data-oid="hg9-6v9"
              />
            </div>

            {/* Tooltip - Optimized for mobile */}
            <div
              className={`absolute space-y-2 rounded-lg bg-background p-4 shadow-lg max-w-[calc(100%-40px)] ${isMobile ? "text-sm" : ""}`}
              style={{
                // On mobile, show tooltip at the top of the chart
                top: isMobile
                  ? "10px"
                  : `${Math.min(data[hoveredIndex]?.viewsY ?? 0, data[hoveredIndex]?.clicksY ?? 0) + 20}px`,
                // On mobile, center the tooltip
                left: isMobile
                  ? "50%"
                  : mouseX >
                      (containerRef.current?.getBoundingClientRect().width ??
                        0) -
                        300
                    ? `${getScaledXPosition(hoveredIndex) - 210}px`
                    : `${getScaledXPosition(hoveredIndex) + 20}px`,
                opacity: mouseX > 0 ? 1 : 0,
                transform: isMobile ? "translateX(-50%)" : "translateX(84px)",
                zIndex: 30,
                transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                willChange: "transform, left, top",
              }}
              data-oid="2s6b9uc"
            >
              <div
                className="flex items-center justify-between gap-8"
                data-oid="r3bqjlz"
              >
                <span
                  className="text-sm text-muted-foreground"
                  data-oid="ov.mizw"
                >
                  Views
                </span>
                <div className="flex items-center gap-2" data-oid="thxss-8">
                  <span className="font-semibold" data-oid="klpayxr">
                    {data[hoveredIndex]?.views.toLocaleString()}
                  </span>
                  <Badge
                    variant="secondary"
                    className={`${
                      data[hoveredIndex]?.viewsGrowth >= 0
                        ? "bg-blue-500/10 text-blue-500"
                        : "bg-red-500/10 text-red-500"
                    }`}
                    data-oid=":43pg-:"
                  >
                    {data[hoveredIndex]?.viewsGrowth >= 0 ? "+" : ""}
                    {data[hoveredIndex]?.viewsGrowth}%
                  </Badge>
                </div>
              </div>
              <div
                className="flex items-center justify-between gap-8"
                data-oid="8bzeln1"
              >
                <span
                  className="text-sm text-muted-foreground"
                  data-oid="gkd:670"
                >
                  Clicks
                </span>
                <div className="flex items-center gap-2" data-oid="jg3e9gu">
                  <span className="font-semibold" data-oid="g:iler6">
                    {data[hoveredIndex]?.clicks.toLocaleString()}
                  </span>
                  <Badge
                    variant="secondary"
                    className={`${
                      data[hoveredIndex]?.clicksGrowth >= 0
                        ? "bg-teal-500/10 text-teal-500"
                        : "bg-red-500/10 text-red-500"
                    }`}
                    data-oid=".i55yf9"
                  >
                    {data[hoveredIndex]?.clicksGrowth >= 0 ? "+" : ""}
                    {data[hoveredIndex]?.clicksGrowth}%
                  </Badge>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default AnalyticsDashboard;
